/// <amd-module name="DistributedTaskUI/Logs/AgentAcquisition" />
import * as React from "react";

import { ILicenseInfo } from "DistributedTaskUI/Logs/Logs.Types";
import { LogsViewUtility } from "DistributedTaskUI/Logs/LogsViewUtility";
import * as Resources from "DistributedTaskUI/Logs/Resources";

import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

import { TaskAgentJobRequest, TaskAgentStatus } from "DistributedTask/Client/Contracts/TaskAgent";

import { IVssPageContext } from "VSS/Platform/Context";
import { localeFormat } from "VSS/Core/Util/String";
import { Status, StatusSize, Statuses } from "VSSUI/Status";

import "VSS/LoaderPlugins/Css!DistributedTaskUI/Logs/AgentAcquisition";

const LogsViewUtility2 = LogsViewUtility; // This is for decreasing size of the minified script

export interface IAgentAcquisitionBaseProps {
    // Agent job request
    jobRequest?: TaskAgentJobRequest;
    // License related information
    licenseInfo?: ILicenseInfo;
    // flag to indicate whether user is anonymous
    isAnonymousUser?: boolean;
    // Queue position
    queuePosition?: number;
    // flag to indicate whether to hide license info section
    hideLicenseInfoSection?: boolean;
    // Indicate whether pool is private or hosted
    isHostedPool?: boolean;
}

export interface IAgentAcquisitionProps extends IAgentAcquisitionBaseProps {
    // Queue id against which job will run
    queueId?: number;
    // Agent name
    agentName?: string;
    // Current page context
    pageContext?: IVssPageContext;
    // Should the agent acquisition header be hidden
    hideHeader?: boolean;
    // Class name for overriding styles of container
    containerCssClass?: string;
    // Class name for overriding styles of pre-requisite section
    preRequisiteSectionCssClass?: string;
}

interface IAgentAcquisitionPreRequisiteProps {
    preRequisiteTitle: string;
    preRequisiteSubText?: string;
    preRequisiteLinkName?: string;
    preRequisiteLinkUrl?: string;
    getPreRequisiteLearnMoreMarkDown?: () => React.ReactNode;
    getIconSection?: () => React.ReactNode;
    cssClass?: string;
}

interface IPreRequisiteTextAndIcon {
    headingText: string;
    subText: string;
    linkName: string;
    getAgentIconSection: () => JSX.Element;
}

// React stateless functional component
const AgentAcquisitionPreRequisite: React.StatelessComponent<IAgentAcquisitionPreRequisiteProps> = (props: IAgentAcquisitionPreRequisiteProps): JSX.Element => {
    return (
        <div className={css("agent-acquisition-preRequisite-section", "flex", props.cssClass)} aria-live={"assertive"}>
            <div className="agent-acquisition-preRequisite-icon-section">
                {props.getIconSection && props.getIconSection()}
            </div>
            <div className="agent-acquisition-preRequisite-header">
                <div className="agent-acquisition-preRequisite-title">
                    {props.preRequisiteTitle}
                </div>
                {
                    props.getPreRequisiteLearnMoreMarkDown &&
                    <div className="agent-acquisition-preRequisite-learn-more">
                        {props.getPreRequisiteLearnMoreMarkDown()}
                    </div>
                }
                {(props.preRequisiteSubText || props.preRequisiteLinkName) &&
                    <div className="agent-acquisition-preRequisite-subtext-section">
                        {
                            props.preRequisiteSubText &&
                            <span>{props.preRequisiteSubText}</span>
                        }
                        {props.preRequisiteSubText && props.preRequisiteLinkName && < div className="agent-acquisition-preRequisite-subtext-separator">&middot;</div>}
                        {
                            props.preRequisiteLinkName &&
                            <div className="agent-acquisition-preRequisite-link">
                                <Link
                                    className={"agent-acquisition-link"}
                                    href={props.preRequisiteLinkUrl}
                                    target="_blank"
                                    rel={"noopener noreferrer"}>
                                    {props.preRequisiteLinkName}
                                    <span className="agent-acquisition-preRequisite-external-link bowtie-icon bowtie-navigate-external" />
                                </Link>

                            </div>
                        }
                    </div>
                }
            </div>
        </div >);
};

export class AgentAcquisition extends React.Component<IAgentAcquisitionProps, {}> {

    /**
     * Render agent acquisition prerequisites, will render license information and agent initialization section
     * No need to render license section for anonymous user
     */
    public render() {
        const showLicenseInfoSection: boolean = this._shouldShowLicenseInfoSection();
        return (
            <div className={css("agent-acquisition-container", this.props.containerCssClass)}>
                {!this.props.hideHeader &&
                    <div className="agent-acquisition-header">
                        {Resources.AgentAcquisitionHeaderTitle}
                    </div>
                }
                <div className="agent-acquisition-prerequisites">
                    {showLicenseInfoSection && this._getLicenseSection()}
                    {!showLicenseInfoSection && this._getAgentInitializationSection()}
                </div>
            </div>);
    }

    private _shouldShowLicenseInfoSection(): boolean {
        // Show License Info section only when all the following condition satisfied
        // 1) When Job is already queued and job request present, otherwise render agent section which shows waiting for request to be queued
        // 2) hideLicenseInfoSection props is set to false
        // 3) License is not acquired yet
        // 4) User is not anonymous
        return (this.props.jobRequest && !this.props.hideLicenseInfoSection && !this._isLicenseAcquired() && !this.props.isAnonymousUser) || false;
    }

    /**
     * If licenseCount is not available it means we are still making service call to get the license info, show loading experience
     * If licenseCount is available, show license info to the user.
     */
    private _getLicenseSection(): JSX.Element {
        let title: string;
        let getIconSection: () => JSX.Element = this._getLoadingSpinner;
        const concurrentJobsLimitUrl = LogsViewUtility2.getConcurrentJobsLimitUrl(this.props.pageContext);
        if (this._isLicenseAcquired()) {
            title = Resources.JobAuthorizedToStart;
            getIconSection = this._getPreRequisiteAcquiredIconSection;
        } else {
            title = Resources.WaitingForRunningJobsToFinish;

            const queuePosition = this.props.queuePosition || 0;
            const eligibleAgentsCount = this._getEligibleAgentsCount();
            
            if (eligibleAgentsCount > 0 && queuePosition >= 0) {
                getIconSection = this._getPositionIconSection;
            }
        }

        const learnMoreMarkDown = () => !this.props.isHostedPool ? (
            <div>
                {Resources.ConcurrencyLimitsInformation}
                <Link
                    className={"learn-more"}
                    href={"https://aka.ms/concurrency-limits"}
                    rel={"noopener noreferrer"}
                    target={"_blank"}>
                    {Resources.ConcurrencyLimitsLearnMore}
                </Link>
                <span className={"bowtie-icon bowtie-navigate-external"} />
            </div>
        ) : null;

        return <AgentAcquisitionPreRequisite
            preRequisiteLinkName={Resources.ManageConcurrencyLimit}
            preRequisiteLinkUrl={concurrentJobsLimitUrl}
            preRequisiteTitle={title}
            getPreRequisiteLearnMoreMarkDown={learnMoreMarkDown}
            getIconSection={getIconSection}
            cssClass={this.props.preRequisiteSectionCssClass}
        />;
    }

    private _isLicenseAcquired(): boolean {
        // License is acquired in case of following cases:
        // 1. If the agent is already acquired, its fair to assume that license must got acquired as agent already acquired for the job request.
        // 2. When usedLicenseCount is less than totalLicenseCount
        // 3. If totalLicenseCount is 0 then check whether usedMinutes is less than totalMinutes
        if (this._isAgentAcquired()) {
            return true;
        }

        if (this.props.licenseInfo) {
            const usedLicenseCount: number = this.props.licenseInfo.usedLicenseCount || 0;
            const totalLicenseCount: number = this.props.licenseInfo.totalLicenseCount || 0;
            const usedMinutes: number = this.props.licenseInfo.usedMinutes || 0;
            const totalMinutes: number = this.props.licenseInfo.totalMinutes || 0;
            if (totalLicenseCount > 0) {
                return usedLicenseCount < totalLicenseCount;
            } else if (totalLicenseCount === 0) {
                return usedMinutes < totalMinutes;
            }
        }

        return false;
    }

    private _isAgentAcquired(): boolean {
        // If the agent is assigned for the job request then reserved agent will be populated
        return (this.props.jobRequest && this.props.jobRequest.reservedAgent && this.props.jobRequest.reservedAgent.id > 0) || false;
    }

    private _getLoadingSpinner = (): JSX.Element => {
        return <Spinner className={"agent-acquisition-spinner"} size={SpinnerSize.large} />;
    }

    /**
     * 1) If user is anonymous then only show acquiring agent with the spinner
     * 2) If queueId is not available then show information to user saying 'waiting for the request to be queued' with spinner
     * 3) If agentName available and we are still in this page, it means waiting for the agent to run the job with success icon
     * 4) If eligibleAgents props available, then show information to user that waiting for one of the eligible agents to acquire with spinner
     * 5) If eligibleAgents count is 0, it means all agents are disabled, show message accordingly.
     * 6) Otherwiser just show generic message with spinner.
     */
    private _getAgentInitializationSection(): JSX.Element {
        if (this.props.isAnonymousUser) {
            return <AgentAcquisitionPreRequisite
                preRequisiteTitle={Resources.WaitingForAvailableAgent}
                getIconSection={this._getLoadingSpinner}
                cssClass={this.props.preRequisiteSectionCssClass}
            />;
        }

        if (this._isValidQueueId() && this.props.jobRequest) {
            return this._getAgentAcquisitionSectionForQueuedRequest();
        } else {
            return this._getAgentAcquisitionSectionForWaitingForQueuedRequest();
        }
    }

    private _getAgentAcquisitionSectionForQueuedRequest(): JSX.Element {
        const queueUrl: string = LogsViewUtility2.getQueueUrl(this.props.queueId, this.props.pageContext);
        const agentAcquisitionInfo = this._getAgentAcquisitionTextAndIcon();

        return <AgentAcquisitionPreRequisite
            getIconSection={agentAcquisitionInfo.getAgentIconSection}
            preRequisiteLinkName={agentAcquisitionInfo.linkName}
            preRequisiteLinkUrl={queueUrl}
            preRequisiteTitle={agentAcquisitionInfo.headingText}
            preRequisiteSubText={agentAcquisitionInfo.subText}
            cssClass={this.props.preRequisiteSectionCssClass}
        />;
    }

    private _getAgentAcquisitionTextAndIcon(): IPreRequisiteTextAndIcon {
        let headingText = Resources.JobsAheadText;
        let subText;
        let getAgentIconSection: () => JSX.Element = this._getLoadingSpinner;
        let linkName: string | null = null;
        const queuePosition = this.props.queuePosition || 0;
        const eligibleAgentsCount = this._getEligibleAgentsCount();

        if (this._isAgentAcquired()) {
            if (!this.props.isHostedPool && this.props.agentName) {
                headingText = localeFormat(Resources.PrivateAgentReadyForJobText, this.props.agentName);
                subText = Resources.AgentInitializingSubText;
            } else {
                headingText = Resources.HostedAgentReadyForJobText;
                subText = Resources.AgentConnectingSubText;
            }
            getAgentIconSection = this._getPreRequisiteAcquiredIconSection;
        } else if (eligibleAgentsCount > 0 && queuePosition > 0) { // If eligibleAgentsCount and queuePosition > 0, then show queue position experience
            subText = this.props.isHostedPool ? Resources.HostedAgentsBusyText : Resources.PrivateAgentsBusyText;
            getAgentIconSection = this._getPositionIconSection;
            linkName = this._getQueueLinkName();
        } else if (eligibleAgentsCount > 0 && queuePosition === 0) {// If eligibleAgentsCount > 0 and queuePosition is 0 it means its waiting for license to acquired
            getAgentIconSection = this._getPositionIconSection;
            linkName = this._getQueueLinkName();
        } else if (eligibleAgentsCount === 0) {// If eligibleAgentsCount is 0, it means all agents are disabled 
            headingText = Resources.WaitingForAvailableAgent;
            subText = Resources.AllAgentsDisabled;
            linkName = this._getQueueLinkName();
        } else {
            headingText = Resources.LoadingAgentInfo;
        }

        return {
            headingText: headingText,
            subText: subText,
            linkName: linkName,
            getAgentIconSection: getAgentIconSection
        } as IPreRequisiteTextAndIcon;
    }

    private _getQueueLinkName(): string {
        return this.props.isHostedPool ? Resources.HostedQueue : Resources.PrivateQueue;
    }

    private _getEligibleAgentsCount(): number {
        let eligibleAgentsCount = -1;
        if (this.props.jobRequest) {
            // When there is no matched agents then object doesn't contain matcheAgents field
            // return 0 in that case
            const matchedAgents = this.props.jobRequest.matchedAgents;
            const eligibleAgents = matchedAgents && matchedAgents.filter((agent) => {
                return agent.enabled === true && agent.status === TaskAgentStatus.Online;
            });

            eligibleAgentsCount = eligibleAgents && eligibleAgents.length || 0;
        }

        return eligibleAgentsCount;
    }

    private _getPositionIconSection = (): JSX.Element => {
        const queuePosition = this.props.queuePosition || "--";
        return <div className="queue-position-icon">
            <div className="queue-position-text">{queuePosition}</div>
        </div>;
    }

    private _getPreRequisiteAcquiredIconSection = (): JSX.Element => {
        return <Status {...Statuses.Success} size={StatusSize.xl} />;
    }

    private _getAgentAcquisitionSectionForWaitingForQueuedRequest(): JSX.Element {
        return <AgentAcquisitionPreRequisite
            getIconSection={this._getLoadingSpinner}
            preRequisiteTitle={Resources.AgentRequestQueued}
            cssClass={this.props.preRequisiteSectionCssClass}
        />;
    }

    private _isValidQueueId(): boolean {
        return (this.props.queueId && this.props.queueId > 0) || false;
    }
}
